import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

function geocodeErrorMessage(error?: string | null): string {
  if (!error) return "No results found.";
  switch (error) {
    case "ZERO_RESULTS":
      return "No results found. Try matching the exact location name as on Google Maps (e.g. street, city, state).";
    case "OVER_QUERY_LIMIT":
      return "Too many requests. Please try again later.";
    case "REQUEST_DENIED":
      return "Address search is temporarily unavailable.";
    case "INVALID_REQUEST":
      return "Please enter a valid address.";
    case "UNKNOWN_ERROR":
      return "Something went wrong. Please try again.";
    default:
      return "No results found. Try a different address.";
  }
}

declare global {
  interface Window {
    google?: {
      maps?: {
        Map: new (el: HTMLElement, opts: object) => {
          setCenter: (latLng: { lat: () => number; lng: () => number }) => void;
          setZoom: (z: number) => void;
        };
        Marker: new (opts: { map: unknown; position: { lat: number; lng: number } }) => {
          setMap: (map: unknown) => void;
        };
        LatLng: new (lat: number, lng: number) => { lat: () => number; lng: () => number };
      };
      accounts?: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: { theme?: string; size?: string; type?: string; width?: number }
          ) => void;
        };
      };
    };
  }
}

export interface AddressResult {
  address: string;
  latitude?: number;
  longitude?: number;
}

interface AddressMapSearchProps {
  value: string;
  onChange: (result: AddressResult) => void;
  latitude?: number;
  longitude?: number;
  placeholder?: string;
  error?: string;
  label?: string;
  disabled?: boolean;
}

export function AddressMapSearch({
  value,
  onChange,
  latitude,
  longitude,
  placeholder = "Enter address or use location",
  error,
  label = "Address",
  disabled = false,
}: AddressMapSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapScriptLoaded, setMapScriptLoaded] = useState(false);
  const [locating, setLocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const markerRef = useRef<unknown>(null);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Load Maps JS only when we need to show the map (no Places library = no autocomplete cost)
  useEffect(() => {
    if (!apiKey || latitude == null || longitude == null) return;
    if (window.google?.maps?.Map) {
      setMapScriptLoaded(true);
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      if (window.google?.maps?.Map) setMapScriptLoaded(true);
      else {
        const t = setInterval(() => {
          if (window.google?.maps?.Map) {
            setMapScriptLoaded(true);
            clearInterval(t);
          }
        }, 100);
        return () => clearInterval(t);
      }
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=__addressMapInit`;
    script.async = true;
    script.defer = true;
    (window as Window & { __addressMapInit?: () => void }).__addressMapInit = () =>
      setMapScriptLoaded(true);
    document.head.appendChild(script);
    return () => {
      delete (window as Window & { __addressMapInit?: () => void }).__addressMapInit;
    };
  }, [apiKey, latitude, longitude]);

  useEffect(() => {
    if (!mapScriptLoaded || !apiKey || !mapRef.current || latitude == null || longitude == null)
      return;
    const container = mapRef.current;
    const maps = window.google?.maps;
    if (!maps) return;
    const center = new maps.LatLng(latitude, longitude);

    const initMap = () => {
      if (!container?.isConnected) return;
      if (!mapInstanceRef.current) {
        const map = new maps.Map(container, {
          center: { lat: latitude!, lng: longitude! },
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });
        mapInstanceRef.current = map;
      }
      const map = mapInstanceRef.current as {
        setCenter: (c: unknown) => void;
        setZoom: (z: number) => void;
      };
      map.setCenter(center);
      map.setZoom(15);
      const prev = markerRef.current as { setMap?: (m: unknown) => void } | null;
      if (prev?.setMap) prev.setMap(null);
      markerRef.current = new maps.Marker({
        map: mapInstanceRef.current,
        position: { lat: latitude!, lng: longitude! },
      });
    };
    const rafId = requestAnimationFrame(() => requestAnimationFrame(initMap));
    return () => cancelAnimationFrame(rafId);
  }, [mapScriptLoaded, apiKey, latitude, longitude]);

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setLocateError("Geolocation is not supported by your browser");
      return;
    }
    setLocating(true);
    setLocateError(null);
    setSearchError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        try {
          const data = await api.get<{ address?: string | null; error?: string }>(
            `/geocode?lat=${lat}&lng=${lng}`
          );
          if (data.address) {
            onChange({ address: data.address, latitude: lat, longitude: lng });
          } else {
            onChange({
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              latitude: lat,
              longitude: lng,
            });
          }
        } catch {
          onChange({
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            latitude: lat,
            longitude: lng,
          });
        }
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocateError(
          err.code === 1
            ? "Location access denied. Please enable location in your browser."
            : err.code === 3
              ? "Location request timed out."
              : "Could not get your location."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleSearch = async () => {
    const q = value.trim();
    if (!q) {
      setSearchError("Enter an address to search");
      return;
    }
    setSearching(true);
    setSearchError(null);
    setLocateError(null);
    try {
      const data = await api.get<{
        address?: string | null;
        latitude?: number;
        longitude?: number;
        error?: string;
      }>(`/geocode?address=${encodeURIComponent(q)}`);
      if (data.address && data.latitude != null && data.longitude != null) {
        onChange({ address: data.address, latitude: data.latitude, longitude: data.longitude });
      } else if (data.address) {
        onChange({ address: data.address });
      } else {
        setSearchError(geocodeErrorMessage(data.error));
      }
    } catch {
      setSearchError("Search failed. Try again.");
    } finally {
      setSearching(false);
    }
  };

  const hasCoords = latitude != null && longitude != null;

  return (
    <div className="space-y-1.5 relative z-10">
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex flex-1 items-center rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 min-h-[44px] overflow-hidden focus-within:border-primary-500 dark:focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/20 dark:focus-within:ring-primary-500/30 [&_input]:outline-none [&_input]:focus:outline-none [&_input]:ring-0 [&_input]:border-0 [&_input]:bg-transparent [&_button]:outline-none [&_button]:focus:outline-none [&_button]:ring-0 [&_button]:focus-visible:ring-0">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              onChange({ address: e.target.value });
              setSearchError(null);
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            className="flex-1 min-w-0 min-h-[44px] mr-4 px-4 pr-24 py-2.5 bg-transparent text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none ring-0 disabled:bg-transparent disabled:text-slate-500 dark:disabled:text-slate-500"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearch())}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            <button
              type="button"
              onClick={handleSearch}
              disabled={disabled || searching}
              className="rounded-lg px-2.5 py-1.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors outline-none focus:outline-none ring-0 focus-visible:ring-0"
            >
              {searching ? "…" : "Search"}
            </button>
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={disabled || locating}
              className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-primary-500 dark:hover:text-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed outline-none focus:outline-none ring-0 focus-visible:ring-0"
              title="Use current location"
            >
              {locating ? (
                <span className="block w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {(locateError || searchError) && (
        <p className="text-sm text-amber-600">{locateError || searchError}</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!apiKey && import.meta.env.PROD && (
        <p className="text-sm text-amber-600">
          Set <code className="text-xs bg-amber-50 px-1 py-0.5 rounded">VITE_GOOGLE_MAPS_API_KEY</code> and
          backend <code className="text-xs bg-amber-50 px-1 py-0.5 rounded">GOOGLE_MAPS_API_KEY</code> for
          address search and map.
        </p>
      )}
      {apiKey && hasCoords && (
        <div
          ref={mapRef}
          className="h-48 w-full min-h-[192px] rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden bg-slate-100 dark:bg-slate-800 ring-1 ring-slate-200/50 dark:ring-slate-600/50 shadow-inner"
          style={{ minHeight: 192 }}
          aria-label="Map showing selected location"
        />
      )}
    </div>
  );
}
